import { SdpFormatter } from "@kalyzee/kast-web-sdk/webrtc";
import { findNestedArrayArgument, findUntrimmedActionName } from "./sdpPipeline";

/**
 * SDP formatter pipelines (`settings.emission.sdpFormatter`) are free text run by
 * `SdpFormatter.execPipeline` from inside the WebRTC negotiation. The SDK does not
 * catch what they throw: the error propagates through the SDP middleware, the
 * stream never publishes, and nothing is shown to the user. So a pipeline is
 * dry-run against a sample SDP — at save time in the settings, and again before
 * being handed to the SDK — and a failing one is reported rather than let loose
 * on a live negotiation.
 */

/** The two negotiation sides a pipeline can be attached to. */
export type SdpFormatterField = "offer" | "answer";

export const SDP_FORMATTER_FIELDS: SdpFormatterField[] = ["offer", "answer"];

/**
 * A plausible OvenMedia-shaped offer: session lines plus one audio and one video
 * m-section carrying codecs, codec params, rtcp-fb, extmaps and a bandwidth line.
 * Every action therefore has something to match, so the dry run exercises the
 * real code path instead of a no-op.
 */
const SAMPLE_SDP = [
  "v=0",
  "o=- 4611731400430051336 2 IN IP4 127.0.0.1",
  "s=-",
  "t=0 0",
  "a=group:BUNDLE 0 1",
  "m=audio 9 UDP/TLS/RTP/SAVPF 111 103 9 0 8",
  "c=IN IP4 0.0.0.0",
  "b=AS:128",
  "a=rtcp:9 IN IP4 0.0.0.0",
  "a=mid:0",
  "a=extmap:1 urn:ietf:params:rtp-hdrext:ssrc-audio-level",
  "a=sendrecv",
  "a=rtcp-mux",
  "a=rtpmap:111 opus/48000/2",
  "a=rtcp-fb:111 transport-cc",
  "a=fmtp:111 minptime=10;useinbandfec=1",
  "a=rtpmap:103 ISAC/16000",
  "a=rtpmap:9 G722/8000",
  "a=rtpmap:0 PCMU/8000",
  "a=rtpmap:8 PCMA/8000",
  "m=video 9 UDP/TLS/RTP/SAVPF 96 97 98 100",
  "c=IN IP4 0.0.0.0",
  "b=AS:2500",
  "a=rtcp:9 IN IP4 0.0.0.0",
  "a=mid:1",
  "a=extmap:2 http://www.webrtc.org/experiments/rtp-hdrext/abs-send-time",
  "a=extmap:3 urn:3gpp:video-orientation",
  "a=sendrecv",
  "a=rtcp-mux",
  "a=rtpmap:96 VP8/90000",
  "a=rtcp-fb:96 goog-remb",
  "a=rtcp-fb:96 transport-cc",
  "a=rtcp-fb:96 ccm fir",
  "a=rtcp-fb:96 nack",
  "a=rtcp-fb:96 nack pli",
  "a=rtpmap:97 rtx/90000",
  "a=fmtp:97 apt=96",
  "a=rtpmap:98 VP9/90000",
  "a=rtpmap:100 H264/90000",
  "a=fmtp:100 level-asymmetry-allowed=1;packetization-mode=1;profile-level-id=42e01f",
].join("\r\n");

/**
 * Dry-runs `pipeline`. Returns the failure message, or `undefined` when it is
 * empty (nothing to apply) or ran clean.
 */
export const validateSdpPipeline = (pipeline: string | undefined): string | undefined => {
  if (!pipeline?.trim()) return undefined;
  // Both checked first: the errors the SDK raises for them name no cause (an
  // invisible space in "Action unknown", a raw JSON parse error).
  const padded = findUntrimmedActionName(pipeline);
  if (padded) {
    return `les espaces autour du \`|\` font partie du nom de l'action, ici \`${padded}\``
      + " — écrire les actions collées au séparateur";
  }
  const nested = findNestedArrayArgument(pipeline);
  if (nested) {
    return `l'action \`${nested}\` reçoit un argument tableau, que le pipeline texte ne sait pas lire`
      + " — seule la forme callback le permet";
  }
  try {
    new SdpFormatter(SAMPLE_SDP).execPipeline(pipeline);
    return undefined;
  } catch (err) {
    return (err as any)?.message || String(err);
  }
};

export interface SdpFormatterSettings {
  offer?: string;
  answer?: string;
}

export interface SdpPipelineError {
  field: SdpFormatterField;
  message: string;
}

/** Dry-runs both sides. Empty when every configured pipeline is applicable. */
export const validateSdpFormatterSettings = (
  sdpFormatter: SdpFormatterSettings | undefined,
): SdpPipelineError[] => {
  if (!sdpFormatter) return [];
  const errors: SdpPipelineError[] = [];
  for (const field of SDP_FORMATTER_FIELDS) {
    const message = validateSdpPipeline(sdpFormatter[field]);
    if (message) errors.push({ field, message });
  }
  return errors;
};

export type SdpFormatterCallback = (formatter: SdpFormatter) => void;

/**
 * Turns the configured pipelines into callbacks for the SDK's
 * `emissionManager.sdp.formatter`, so a bad pipeline can no longer break a
 * negotiation:
 *
 * - one that fails its dry run is dropped and reported — the SDP goes out
 *   untouched, exactly as if no formatter were configured;
 * - one that passes still runs guarded, and a residual failure is reported
 *   instead of thrown. The formatter mutates its lines in place, so the SDP may
 *   then carry the actions that ran before the failing one: publishing a
 *   partially formatted SDP beats not publishing at all.
 *
 * Returns `undefined` when nothing applicable is left — what the SDK reads as
 * "no formatter".
 */
export const buildSdpFormatters = (
  config: SdpFormatterSettings | undefined,
  onError?: (error: SdpPipelineError) => void,
): { offer?: SdpFormatterCallback; answer?: SdpFormatterCallback } | undefined => {
  if (!config) return undefined;
  const callbacks: { offer?: SdpFormatterCallback; answer?: SdpFormatterCallback } = {};
  for (const field of SDP_FORMATTER_FIELDS) {
    const pipeline = config[field];
    if (!pipeline?.trim()) continue;
    const invalid = validateSdpPipeline(pipeline);
    if (invalid) {
      onError?.({ field, message: invalid });
      continue;
    }
    callbacks[field] = (formatter: SdpFormatter) => {
      try {
        formatter.execPipeline(pipeline);
      } catch (err) {
        onError?.({ field, message: (err as any)?.message || String(err) });
      }
    };
  }
  if (!callbacks.offer && !callbacks.answer) return undefined;
  return callbacks;
};
